import React from 'react';
import { Box } from '@mui/material';
import type { IntegrationConfig } from '../../../services/integrationApi';
import ShopifyCatalogScan from '../../ShopifyCatalogScan';
import ShopifyListingHolds from '../../ShopifyListingHolds';
import ShopifySpecMetafieldsSection, { SpecMetafieldRow } from '../../ShopifySpecMetafieldsSection';
import SpecMetafieldsV2View from './SpecMetafieldsV2View';
import { SpecCrosswalkBar } from './SpecCrosswalkControls';
import ShopifyMetafieldMappingSection, { MetafieldMappingRow } from '../../ShopifyMetafieldMappingSection';
import ListingMatchesV2View from './ListingMatchesV2View';

/**
 * The two sub-tabs View A does not own. `MappingV2Panel` mounts `specView` / `matchesView`, which
 * default to the v2 views below. Today's components stay exported as `DefaultSpecView` /
 * `DefaultMatchesView` (fallbacks); a caller can pass any component through the panel's props.
 */

export interface SpecViewProps {
  integration: IntegrationConfig;
  /** The unsaved spec metafield rows. Edits go through `onChange` and are written by the page's Save. */
  rows: SpecMetafieldRow[];
  onChange: (rows: SpecMetafieldRow[]) => void;
  onOpenProductFields?: () => void;
  /**
   * MOT-144 (flag `shopify_metafield_mapping`): the unsaved rows of the general mapping, shown below the spec
   * table and written by the same Save. Absent = the table is not rendered.
   */
  mappingRows?: MetafieldMappingRow[];
  onMappingChange?: (rows: MetafieldMappingRow[]) => void;
  /** The plan lacks `customMetafieldMapping`: the price-bearing sources render locked (MOT-574). */
  mappingPlanLocked?: boolean;
}

/** The general mapping table under a spec view, when the parent hands it rows. */
const MappingTable: React.FC<SpecViewProps> = ({ integration, mappingRows, onMappingChange, mappingPlanLocked }) =>
  mappingRows && onMappingChange ? (
    <ShopifyMetafieldMappingSection
      integrationId={integration.id}
      rows={mappingRows}
      onChange={onMappingChange}
      planLocked={Boolean(mappingPlanLocked)}
    />
  ) : null;

export interface MatchesViewProps {
  integration: IntegrationConfig;
}

export const DefaultSpecView: React.FC<SpecViewProps> = (props) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
    <SpecCrosswalkBar integrationId={props.integration.id} rows={props.rows} onChange={props.onChange} />
    <ShopifySpecMetafieldsSection
      integrationId={props.integration.id}
      rows={props.rows}
      onChange={props.onChange}
      onOpenProductFields={props.onOpenProductFields}
    />
    <MappingTable {...props} />
  </Box>
);

/** Listing-match decisions apply immediately (`applyMatches`), so this view has no unsaved state. */
export const DefaultMatchesView: React.FC<MatchesViewProps> = ({ integration }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
    <ShopifyCatalogScan integration={integration} />
    <ShopifyListingHolds integrationId={integration.id} />
  </Box>
);

/** View B behind the slot contract: it takes the integration's id, not the integration. */
export const SpecMetafieldsV2Slot: React.FC<SpecViewProps> = (props) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
    <SpecMetafieldsV2View integrationId={props.integration.id} rows={props.rows} onChange={props.onChange} onOpenProductFields={props.onOpenProductFields} />
    <MappingTable {...props} />
  </Box>
);

/** View C already takes `{ integration }`. */
export const MatchesV2Slot: React.FC<MatchesViewProps> = ({ integration }) => <ListingMatchesV2View integration={integration} />;

